import { useEffect, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { axiosInstance } from "../../../utils/networking";
import { fmt } from "./useReportFilter";

/** GET /reports/metrics contract. */
export type MetricUnit = "count" | "hours";

export interface MetricCatalogEntry {
  id: string;
  label: string;
  unit: MetricUnit;
  section: string;
  core: boolean;
  definition?: string;
}

/**
 * One column's numbers. Most metrics are plain numbers keyed by catalog id;
 * `referralTypes` and `assistanceRequested` are keyed-object breakdowns that
 * the table renders as their own sections. Both are optional so the client
 * keeps working against a server that has not been redeployed yet — a missing
 * breakdown simply means that section is absent.
 */
export interface MetricBucket {
  [id: string]: any;
  referralTypes?: { [type: string]: number };
  assistanceRequested?: { [category: string]: number };
}

export interface MetricGroup {
  key: string;
  label: string;
  metrics: MetricBucket;
}

export interface MetricsResponse {
  range: { start: string | null; end: string | null };
  catalog: MetricCatalogEntry[];
  groups: MetricGroup[];
  total: MetricBucket;
  /**
   * Org-wide total for the range, whoever asks and whatever `consultant` is
   * (`groups` / `total` stay scoped). Only sent for `programTotal=1`, and
   * absent on a server that has not shipped it yet.
   */
  programTotal?: MetricBucket;
}

export type MetricGroupBy = "consultant" | "month" | "none";

/** Tile / chart order for the nine headline metrics. */
export const CORE_METRIC_IDS = [
  "newClients",
  "initialContacts",
  "businessAssessments",
  "interactions",
  "resourceReferrals",
  "b2bReferrals",
  "finalizedB2b",
  "closedCases",
  "activeClients",
];

/** Fallback labels so tiles still read sensibly if the catalog is unavailable. */
export const CORE_METRIC_FALLBACK_LABELS: { [id: string]: string } = {
  newClients: "New Clients",
  initialContacts: "Initial Contacts",
  businessAssessments: "Business Assessments",
  interactions: "Interactions",
  resourceReferrals: "Resource Referrals",
  b2bReferrals: "B2B Referrals",
  finalizedB2b: "Finalized B2B",
  closedCases: "Closed Cases",
  activeClients: "Active Clients",
};

/**
 * The nine core metrics in chart order, labelled from the catalog (falling
 * back to `CORE_METRIC_FALLBACK_LABELS`). Shared by the Program Statistics
 * section and the dashboard's stats card so their bars cannot drift apart.
 */
export const coreMetricsFromCatalog = (
  catalog: MetricCatalogEntry[] | null | undefined,
): { id: string; label: string }[] => {
  const labels = new Map(
    (catalog || []).map((entry) => [entry.id, entry.label]),
  );
  return CORE_METRIC_IDS.map((id) => ({
    id,
    label: labels.get(id) || CORE_METRIC_FALLBACK_LABELS[id] || id,
  }));
};

export const metricNumber = (
  bucket: { [id: string]: any } | undefined | null,
  id: string,
): number => {
  const v = bucket ? bucket[id] : 0;
  return typeof v === "number" && !Number.isNaN(v) ? v : 0;
};

export const formatMetric = (value: number, unit?: MetricUnit) =>
  unit === "hours"
    ? value.toLocaleString(undefined, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })
    : value.toLocaleString();

export interface MetricsQuery {
  start: Dayjs | null;
  end: Dayjs | null;
  consultant?: string;
  groupBy: MetricGroupBy;
  /** Also ask for the org-wide `programTotal` bucket. */
  programTotal?: boolean;
  /** Skip the request entirely (e.g. tab not visible yet). */
  enabled?: boolean;
  /**
   * Show the cached numbers at once but re-fetch behind them on mount
   * (stale-while-revalidate), for a surface such as the dashboard card that
   * is revisited after the user has logged new work. Only consulted when
   * the module cache has an entry; a first load fetches as usual.
   */
  revalidateOnMount?: boolean;
}

export const metricsCacheKey = (q: MetricsQuery) =>
  `${q.groupBy}|${fmt(q.start)}|${fmt(q.end)}|${q.consultant || ""}${
    q.programTotal ? "|programTotal" : ""
  }`;

// Module-level cache: switching tabs re-renders a lot, and the same filter key
// should not re-hit an expensive aggregation.
const cache = new Map<string, MetricsResponse>();
const inflight = new Map<string, Promise<MetricsResponse>>();

export const clearMetricsCache = () => {
  cache.clear();
  inflight.clear();
};

export const fetchMetrics = (q: MetricsQuery): Promise<MetricsResponse> => {
  const key = metricsCacheKey(q);
  const cached = cache.get(key);
  if (cached) return Promise.resolve(cached);
  const pending = inflight.get(key);
  if (pending) return pending;

  const params: { [k: string]: string } = { groupBy: q.groupBy };
  if (q.start) params.start = fmt(q.start);
  if (q.end) params.end = fmt(q.end);
  if (q.consultant) params.consultant = q.consultant;
  if (q.programTotal) params.programTotal = "1";

  const promise = axiosInstance
    .get<MetricsResponse>("/reports/metrics", { params })
    .then((res) => {
      const data = res.data;
      cache.set(key, data);
      return data;
    })
    .finally(() => {
      inflight.delete(key);
    });
  inflight.set(key, promise);
  return promise;
};

export const describeMetricsError = (err: any): string => {
  const status = err?.response?.status;
  if (status === 403) return "You are not authorized to view these metrics.";
  if (status === 404)
    return "The metrics endpoint is not available yet (GET /reports/metrics).";
  return (
    err?.response?.data?.message || err?.message || "Failed to load metrics."
  );
};

export interface UseMetricsResult {
  data: MetricsResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export const useMetrics = (q: MetricsQuery): UseMetricsResult => {
  const enabled = q.enabled !== false;
  const key = metricsCacheKey(q);
  const [data, setData] = useState<MetricsResponse | null>(
    () => cache.get(key) || null,
  );
  const [loading, setLoading] = useState(enabled && !cache.has(key));
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const cached = cache.get(key);
    if (cached && nonce === 0) {
      setData(cached);
      setLoading(false);
      setError(null);
      if (q.revalidateOnMount) {
        // Drop the entry so fetchMetrics really hits the server; the cached
        // numbers stay on screen until the fresh ones land, and a failure
        // keeps them rather than blanking the card.
        cache.delete(key);
        fetchMetrics(q)
          .then((res) => {
            if (!cancelled) setData(res);
          })
          .catch(() => undefined);
      }
      return () => {
        cancelled = true;
      };
    }
    // A different filter key means the on-screen numbers are for the wrong
    // range — drop them so the caller shows its loading state, not stale data.
    setData(null);
    setLoading(true);
    setError(null);
    fetchMetrics(q)
      .then((res) => {
        if (cancelled) return;
        setData(res);
      })
      .catch((err) => {
        if (cancelled) return;
        setData(null);
        setError(describeMetricsError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, nonce]);

  return {
    data,
    loading,
    error,
    refresh: () => {
      cache.delete(key);
      setNonce((n) => n + 1);
    },
  };
};

// ---------------------------------------------------------------------------
// Compare to previous period
// ---------------------------------------------------------------------------

const EMPTY_RESULT: UseMetricsResult = {
  data: null,
  loading: false,
  error: null,
  refresh: () => undefined,
};

export interface UseMetricsCompareResult {
  current: UseMetricsResult;
  /** Always defined; `data` is null when comparison is off or unavailable. */
  previous: UseMetricsResult;
}

/**
 * The same query run twice — once for the active range, once for the shifted
 * previous-period range. No server change: it is the existing
 * `/reports/metrics` endpoint through the existing module-level cache, so
 * flipping Compare off and on again is free.
 *
 * Pass `previousRange: null` (compare off, or an "All time" range) to skip the
 * second request entirely.
 */
export const useMetricsWithCompare = (
  q: MetricsQuery,
  previousRange: { start: Dayjs | null; end: Dayjs | null } | null,
): UseMetricsCompareResult => {
  const current = useMetrics(q);
  const previousEnabled = q.enabled !== false && previousRange !== null;
  const previous = useMetrics({
    ...q,
    start: previousRange ? previousRange.start : q.start,
    end: previousRange ? previousRange.end : q.end,
    enabled: previousEnabled,
  });
  return { current, previous: previousEnabled ? previous : EMPTY_RESULT };
};

// ---------------------------------------------------------------------------
// Monthly trend (sparklines)
// ---------------------------------------------------------------------------

/** Most recent months a sparkline will plot; older buckets are dropped. */
export const TREND_MAX_POINTS = 36;

/** Whole months touched by a range; an open-ended range counts as "many". */
export const monthSpan = (start: Dayjs | null, end: Dayjs | null): number => {
  if (!start) return TREND_MAX_POINTS;
  const last = (end || dayjs()).startOf("month");
  return Math.max(1, last.diff(start.startOf("month"), "month") + 1);
};

export interface TrendResult {
  /** True once monthly data for a ≥2-month range has loaded. */
  available: boolean;
  loading: boolean;
  /** Ascending month keys ("YYYY-MM"). */
  months: string[];
  /** Ascending display labels ("Jan 2026"). */
  labels: string[];
  /** Ascending values for one metric id, gaps filled with 0. */
  series: (metricId: string) => number[];
}

const EMPTY_TREND: TrendResult = {
  available: false,
  loading: false,
  months: [],
  labels: [],
  series: () => [],
};

/**
 * One extra `groupBy=month` call over the active range, reshaped into dense
 * ascending series for `Sparkline`. Skipped (and `available: false`) when the
 * range spans fewer than two months — a one-point sparkline says nothing.
 */
export const useMonthlyTrend = (q: {
  start: Dayjs | null;
  end: Dayjs | null;
  consultant?: string;
  enabled?: boolean;
}): TrendResult => {
  const spans = monthSpan(q.start, q.end) >= 2;
  const enabled = q.enabled !== false && spans;
  const { data, loading } = useMetrics({
    start: q.start,
    end: q.end,
    consultant: q.consultant,
    groupBy: "month",
    enabled,
  });

  return useMemo(() => {
    if (!enabled) return EMPTY_TREND;
    if (!data) return { ...EMPTY_TREND, loading };

    const byMonth = new Map<string, MetricGroup>();
    (data.groups || []).forEach((g) => byMonth.set(String(g.key), g));

    // The server only emits months that have activity — walk the calendar so
    // an empty month reads as a zero rather than disappearing from the line.
    const keys = Array.from(byMonth.keys()).sort();
    const firstKey = q.start ? q.start.format("YYYY-MM") : keys[0];
    const lastKey = q.end
      ? q.end.format("YYYY-MM")
      : keys[keys.length - 1] || dayjs().format("YYYY-MM");
    const months: string[] = [];
    if (firstKey && lastKey) {
      let cursor = dayjs(`${firstKey}-01`);
      const stop = dayjs(`${lastKey}-01`);
      while (
        months.length < 600 &&
        (cursor.isBefore(stop, "month") || cursor.isSame(stop, "month"))
      ) {
        months.push(cursor.format("YYYY-MM"));
        cursor = cursor.add(1, "month");
      }
    }
    const trimmed =
      months.length > TREND_MAX_POINTS
        ? months.slice(months.length - TREND_MAX_POINTS)
        : months;

    if (trimmed.length < 2) return { ...EMPTY_TREND, loading };

    return {
      available: true,
      loading,
      months: trimmed,
      labels: trimmed.map((m) => dayjs(`${m}-01`).format("MMM YYYY")),
      series: (metricId: string) =>
        trimmed.map((m) => metricNumber(byMonth.get(m)?.metrics, metricId)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, enabled, fmt(q.start), fmt(q.end)]);
};

export default useMetrics;
